import { base, dogechain, mainnet, shibarium } from "viem/chains";
// import { dogechain as dogewagmi, shibarium as shibwagmi } from '@reown/appkit/networks';
import type { BlockchainExplorers, BlockchainTokensType } from "../types";
import type { ChainSettingsRecord, EVMChainSettingType } from "./types";
import { http, type Address } from "viem";
import type { FarmChainIds } from "../project-specific/farms";
import { RequiredAggregatorChainType } from "../project-specific";

export const chainDefaults = {
  appkitChains: [shibarium, dogechain, base, mainnet],
  appChains: [shibarium, dogechain, base, mainnet] as const,
  // chainTransports are only used for wagmi config - not needed for server-side rendering
  chainTransports: {
    [109]: http("https://rpc.shibarium.shib.io"),
    [2000]: http("https://rpc.dogechain.dog"),
  },
  chainImages: {
    109: "https://chewyswap.dog/images/chains/109.png",
    2000: "https://chewyswap.dog/images/chains/2000.png",
    8453: "https://d2f70xi62kby8n.cloudfront.net/bridge/icons/networks/base.svg?auto=compress%2Cformat",
    1: "https://d2f70xi62kby8n.cloudfront.net/bridge/icons/networks/ethereum.svg?auto=compress%2Cformat",
  },
  defaultChain: shibarium,
};

// EVM Chain Id's Supported by Staking Dapp
export enum ChainId {
  SHIBARIUM = 109,
  DOGECHAIN = 2000,

  BASE = 8453,

  ETH = 1,
  // Add more Chain ID's as necessary
}

// ChainIdNum is a type alias for the 'id' property of each chain in the appChains array
export type ChainIdNum = (typeof chainDefaults.appChains)[number]["id"];

// ChainType is a type alias that combines different chain-related types:
// - ChainId: Enum of supported chain IDs
// - ChainIdNum: Type alias for the 'id' property of each chain in the appChains array
// - FarmChainIds: Additional chain IDs specific to farming
export type ChainType = ChainId | ChainIdNum;

/**
 * ChainAllType is a type alias that extends ChainType to include any number.
 * This is useful for scenarios where a chain ID might be represented as a number
 * that is not necessarily part of the predefined ChainType.
 */
export type ChainAllType = ChainType | number;

export const defaultNativeAddress: Address =
  "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE";

// Add rate limiting types
export interface RPCRateLimitSettings {
  batchSize?: number; // How many requests to batch together
  batchInterval?: number; // Time in ms between batches
  maxRequestsPerSecond?: number; // Rate limit for requests
  retryDelay?: number; // Time in ms to wait before retrying
  maxRetries?: number; // Maximum number of retry attempts

  maxTokens?: number;
  refillRate?: number;
  batchWait?: number;
}

// Safe defaults for RPC rate limiting
export const defaultRPCSettings: RPCRateLimitSettings = {
  batchSize: 20,
  batchInterval: 300,
  maxRequestsPerSecond: 4,
  retryDelay: 1000,
  maxRetries: 3,
  refillRate: 5,
  batchWait: 300,
};

// Update ChainSettingsRecord type
export interface ChainSettings {
  // ... existing chain settings ...
  rpcSettings?: RPCRateLimitSettings;
}

export const chainSettings: ChainSettingsRecord = {
  [ChainId.SHIBARIUM]: {
    chainId: 109,
    chainName: "Shibarium",
    chainSlug: "shibarium",
    nativeCurrency: {
      name: "BONE",
      symbol: "BONE",
      decimals: 18,
      address: defaultNativeAddress,
      logo: "https://raw.githubusercontent.com/ChewySwap/chewy-tokenlist/main/tokens/bone.webp",
    },
    wNative: {
      name: "Wrapped BONE",
      symbol: "WBONE",
      address: "0xC76F4c819D820369Fb2d7C1531aB3Bb18e6fE8d8",
      decimals: 18,
      logo: "https://raw.githubusercontent.com/ChewySwap/chewy-tokenlist/main/tokens/bone.webp",
    },
    nativePriceEndpoints: {
      geckoterminal:
        "https://api.geckoterminal.com/api/v2/networks/eth/tokens/0x9813037ee2218799597d83d4a5b6f3b6778218d9/",
    },
    tokenPriceEndpoints: {
      geckoterminal:
        "https://api.geckoterminal.com/api/v2/simple/networks/shibarium/token_price/",
      geckoStats:
        "https://api.geckoterminal.com/api/v2/networks/shibarium/tokens/multi/",
    },
    nativeApiSetting: {
      network: "eth",
      address: "0x9813037ee2218799597d83d4a5b6f3b6778218d9",
    },
    tokenApiSetting: {
      network: "shibarium",
    },
    yakRouter: "0x0628251c32b0F57F3A4A161A7d7dae6af821e5a2",
    multicall: "0xB585B461950c4A684aDB06f8F03eB1c3410ED2c5",
    rpcUrls: ["https://rpc.shibarium.io"],
    blockExplorerUrl: "https://shibariumscan.io",
    blockTime: 5,
    blocksPerYear: 630720000,
    v3LpContract: "0x8Ab443Be082105AE444337b49E1FD8D23c2631Dc",
    tokenInfoContract: "0x6bC7194cF0b2B36739331300Ec3D79eea24F2432",
    interfaceCheckerContract: "0x9Dcf0d1A4a8318C1E07d74785FFC170d9C79D857",
    fotDetectorContract: "0xfd126B54963981eD1EC313b2912b5454C5672713",
  },
  [ChainId.DOGECHAIN]: {
    chainId: 2000,
    chainName: "Dogechain",
    chainSlug: "dogechain",
    nativeCurrency: {
      name: "Dogecoin",
      symbol: "DOGE",
      decimals: 18,
      address: defaultNativeAddress,
      logo: "https://raw.githubusercontent.com/ChewySwap/chewy-tokenlist/main/tokens/dogecoin.webp",
    },
    wNative: {
      name: "Wrapped Dogecoin",
      symbol: "WDOGE",
      address: "0xB7ddC6414bf4F5515b52D8BdD69973Ae205ff101",
      decimals: 18,
      logo: "https://raw.githubusercontent.com/ChewySwap/chewy-tokenlist/main/tokens/dogecoin.webp",
    },
    nativePriceEndpoints: {
      geckoterminal:
        "https://api.geckoterminal.com/api/v2/networks/eth/tokens/0x4206931337dc273a630d328dA6441786BfaD668f/",
    },
    tokenPriceEndpoints: {
      geckoterminal:
        "https://api.geckoterminal.com/api/v2/simple/networks/dogechain/token_price/",
      geckoStats:
        "https://api.geckoterminal.com/api/v2/networks/dogechain/tokens/multi/",
    },
    nativeApiSetting: {
      network: "eth",
      address: "0x4206931337dc273a630d328dA6441786BfaD668f",
    },
    tokenApiSetting: {
      network: "dogechain",
    },
    yakRouter: "0x771037cf7CBb4F1dF79027b73E3DAC8bc95f80c6",
    multicall: "0x68a8609a60a008EFA633dfdec592c03B030cC508",
    rpcUrls: ["https://rpc.dogechain.dog"],
    blockExplorerUrl: "https://explorer.dogechain.dog",
    blockTime: 5,
    blocksPerYear: 630720000,
    v3LpContract: "0x8Ab443Be082105AE444337b49E1FD8D23c2631Dc",
    tokenInfoContract: "0xF0304CD968e80043328d32656279d60916EBa492",
    interfaceCheckerContract: "0x9C872F3f88D65F264Ea064aA96F38783dB364d83",
    fotDetectorContract: "0x12B5Cb8D47Cfe2AFaF1B59846DDd82e4f9cceE71",
  },
  8453: {
    chainId: 8453,
    chainName: "Base",
    chainSlug: "base",
    nativeCurrency: {
      name: "Ethereum",
      symbol: "ETH",
      decimals: 18,
      address: defaultNativeAddress,
      logo: "https://raw.githubusercontent.com/ChewySwap/chewy-tokenlist/main/tokens/eth.webp",
    },
    wNative: {
      name: "Wrapped Ethereum",
      symbol: "WETH",
      address: "0x4200000000000000000000000000000000000006",
      decimals: 18,
      logo: "https://raw.githubusercontent.com/ChewySwap/chewy-tokenlist/main/tokens/eth.webp",
    },
    nativePriceEndpoints: {
      geckoterminal:
        "https://api.geckoterminal.com/api/v2/networks/base/tokens/0x4200000000000000000000000000000000000006/",
    },
    tokenPriceEndpoints: {
      geckoterminal:
        "https://api.geckoterminal.com/api/v2/simple/networks/base/token_price/",
      geckoStats:
        "https://api.geckoterminal.com/api/v2/networks/base/tokens/multi/",
    },
    nativeApiSetting: {
      network: "base",
      address: "0x4200000000000000000000000000000000000006",
    },
    tokenApiSetting: {
      network: "base",
    },
    yakRouter: "0x10ED43C718714eb63d5aA57B78B54704E256024E",
    multicall: "0xcA11bde05977b3631167028862bE2a173976CA11",
    rpcUrls: ["https://mainnet.infura.io/v3/"],
    blockExplorerUrl: "https://base.blockscout.com",
    blockTime: 5,
    blocksPerYear: 630720000,
    tokenInfoContract: "0x440Ff947DAe7BbCdea938fC9DB1FdFFA41DD14F6",
    v3LpContract: "0x8Ab443Be082105AE444337b49E1FD8D23c2631Dc",
    interfaceCheckerContract: "0xF8aD871D61dD70Ca98E1273CC7b31F7447323bAd",
  },
  [ChainId.ETH]: {
    chainId: 1,
    chainName: "Ethereum",
    chainSlug: "ethereum",
    nativeCurrency: {
      name: "Ethereum",
      symbol: "ETH",
      decimals: 18,
      address: defaultNativeAddress,
      logo: "https://raw.githubusercontent.com/ChewySwap/chewy-tokenlist/main/tokens/eth.webp",
    },
    wNative: {
      name: "Wrapped Ethereum",
      symbol: "WETH",
      address: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2",
      decimals: 18,
      logo: "https://raw.githubusercontent.com/ChewySwap/chewy-tokenlist/main/tokens/eth.webp",
    },
    nativePriceEndpoints: {
      geckoterminal:
        "https://api.geckoterminal.com/api/v2/networks/eth/tokens/0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2/",
    },
    tokenPriceEndpoints: {
      geckoterminal:
        "https://api.geckoterminal.com/api/v2/simple/networks/eth/token_price/",
      geckoStats:
        "https://api.geckoterminal.com/api/v2/networks/eth/tokens/multi/",
    },
    nativeApiSetting: {
      network: "eth",
      address: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2",
    },
    tokenApiSetting: {
      network: "eth",
    },
    yakRouter: "0x10ED43C718714eb63d5aA57B78B54704E256024E",
    multicall: "0xcA11bde05977b3631167028862bE2a173976CA11",
    rpcUrls: ["https://mainnet.infura.io/v3/"],
    blockExplorerUrl: "https://eth.blockscout.com",
    blockTime: 5,
    blocksPerYear: 630720000,
    tokenInfoContract: "0xaf94daaF9493b429a3b07d7C7252697994475889",
    v3LpContract: "0x8Ab443Be082105AE444337b49E1FD8D23c2631Dc",
    interfaceCheckerContract: "0xF8aD871D61dD70Ca98E1273CC7b31F7447323bAd",
  },

  // Add more Chain ID's as necessary
};

export const fallbackChainId = ChainId.SHIBARIUM;

export const fallbackMulticall = undefined;

export const fallbackChainName = "Shibarium";

export const explorerUrl: BlockchainExplorers = {
  [ChainId.SHIBARIUM]: "https://shibariumscan.io",
  2000: "https://explorer.dogechain.dog",
  8453: "https://base.blockscout.com",
  1: "https://eth.blockscout.com",
  // Add more Chain ID's as necessary
};

export const wethAddresses: BlockchainTokensType = {
  [ChainId.SHIBARIUM]: "0xC76F4c819D820369Fb2d7C1531aB3Bb18e6fE8d8",
  2000: "0xB7ddC6414bf4F5515b52D8BdD69973Ae205ff101",
  8453: "0x4200000000000000000000000000000000000006",
  1: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2",
  // Add more Chain ID's as necessary
};

export const multicallAddress: BlockchainTokensType = {
  [ChainId.SHIBARIUM]: "0xB585B461950c4A684aDB06f8F03eB1c3410ED2c5",
  2000: "0x68a8609a60a008EFA633dfdec592c03B030cC508",
  8453: "0xcA11bde05977b3631167028862bE2a173976CA11",
  1: "0xcA11bde05977b3631167028862bE2a173976CA11",
  // Add more Chain ID's as necessary
};

// Helper function to get RPC settings for a chain
export function getRPCSettings(chainId: ChainType): RPCRateLimitSettings {
  const settings = (chainSettings as Record<number, ChainSettings>)[chainId];
  return settings?.rpcSettings ?? defaultRPCSettings;
}

// Efficient EVM-only settings for aggregator functionality
export const aggregatorChainSettings: Record<
  RequiredAggregatorChainType,
  EVMChainSettingType
> = {
  [ChainId.SHIBARIUM]: chainSettings[ChainId.SHIBARIUM] as EVMChainSettingType,
  [2000]: chainSettings[2000] as EVMChainSettingType,
};

// Helper function to normalize chain ID from various sources (wagmi, appkit, etc.)
export function normalizeChainId(
  chainId: number | string | null | undefined
): ChainType | null {
  if (!chainId) return null;

  // If it's already a number and not NaN, return as ChainType
  if (typeof chainId === "number") {
    if (Number.isNaN(chainId)) {
      console.warn("⚠️ normalizeChainId: Received NaN chainId");
      return null;
    }
    return chainId as ChainType;
  }

  // If it's a string, check if it's a mapped Solana chain ID
  if (typeof chainId === "string") {
    // Try to parse as number (for string numbers like "8000001001")
    const parsed = parseInt(chainId, 10);
    if (!Number.isNaN(parsed)) {
      return parsed as ChainType;
    }

    console.warn("⚠️ normalizeChainId: Unknown string chainId:", chainId);
  }

  return null;
}

// Helper function to get chain settings with proper normalization
export function getChainSettings(chainId: number | string | null | undefined) {
  const normalizedChainId = normalizeChainId(chainId);
  return normalizedChainId ? chainSettings[normalizedChainId] : null;
}
